/**
 * Hurdles component that fetches its own data.
 * Uses per-section API endpoints for better performance.
 * Only requires core data (hurdles is in the `data` field).
 */
import { MissionAssessmentFieldName } from '@@types/api.types';
import { FieldProvenanceBadge } from '@components/shared/field-provenance-badge.component';
import { cn } from '@lib/utils';
import { useMissionAssessmentCoreQueryOptions } from '@queries/mission-assessments.query';
import { useQuery } from '@tanstack/react-query';
import { MRL_TRL_SETTINGS } from '@utils/constants';
import type { FunctionComponent } from 'react';
import { MissionAssessmentCoreSkeleton } from './mission-assessment-skeletons.component';

type TechnologyMissionAssessmentHurdlesProps = {
  missionAssessmentId: string;
};

const getHurdleTitle = (type: 'MRL' | 'TRL', score?: number): string => {
  if (!score) return `Hurdles to next ${type}`;
  const levels = type === 'TRL' ? MRL_TRL_SETTINGS.trl.levels : MRL_TRL_SETTINGS.mrl.levels;
  const level = levels.find((lvl) => lvl.level === score);
  return level ? `Hurdles to ${type} ${score} - ${level.label}` : `Hurdles to ${type} ${score}`;
};

export const TechnologyMissionAssessmentHurdlesComponent: FunctionComponent<TechnologyMissionAssessmentHurdlesProps> = (
  props,
) => {
  const { missionAssessmentId } = props;

  const { data: coreData, isLoading } = useQuery(useMissionAssessmentCoreQueryOptions(missionAssessmentId));

  if (isLoading) {
    return <MissionAssessmentCoreSkeleton />;
  }

  const hurdlesData = coreData?.data?.hurdles;
  const { nextMrlScore, nextTrlScore } = hurdlesData || {};
  const provenance = coreData?.provenance ?? undefined;

  const hurdles = [
    {
      description: hurdlesData?.nextTrl,
      provenance: provenance?.[MissionAssessmentFieldName.NEXT_TRL] ?? null,
      title: getHurdleTitle('TRL', nextTrlScore),
    },
    {
      description: hurdlesData?.nextMrl,
      provenance: provenance?.[MissionAssessmentFieldName.NEXT_MRL] ?? null,
      title: getHurdleTitle('MRL', nextMrlScore),
    },
    {
      description: hurdlesData?.supplyChain,
      provenance: provenance?.[MissionAssessmentFieldName.SUPPLY_CHAIN] ?? null,
      title: 'Hurdles to Supply Chain',
    },
  ];

  return (
    <div className="flex flex-col gap-y-4">
      {hurdles.map(({ description, provenance: hurdleProvenance, title }) => (
        <div
          className="space-y-2"
          key={title}
        >
          <p className="flex items-center gap-2 text-gray-600 text-sm font-medium">
            {title}
            <FieldProvenanceBadge provenance={hurdleProvenance} />
          </p>
          <p className={cn('text-gray-600 text-sm whitespace-pre-line', !description && 'text-xs text-gray-500')}>
            {description || 'No data available'}
          </p>
        </div>
      ))}
    </div>
  );
};
